import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Chips } from '@cian/ui-kit';
import { debounce } from '@cian/newbuilding-utils';

import { InputsMultiple } from '../../components/InputsMultiple';
import { priceInputDescriptors, priceTypeDescriptors } from '../../constants/descriptors';
import { isDescriptorActive } from '../../utils/filters';
import { IApplicationState } from '../../types/redux';
import { TTypedThunkDispatch } from '../../types/thunk';
import { getOffersCount } from '../../actions/offersData';
import { updateTags } from '../../actions/filterTags';
import { DEFAULT_INPUT_TIMEOUT } from '../../constants/timeouts';
import { FilterPriceWrapper } from '../../components/Filters/FilterPriceWrapper';

export const PriceFilter: React.FC = React.memo(() => {
  const dispatch = useDispatch<TTypedThunkDispatch>();
  const jsonQuery = useSelector((state: IApplicationState) => state.jsonQuery);

  const debouncedGetOffersCount = React.useMemo(
    () => debounce(DEFAULT_INPUT_TIMEOUT, () => dispatch(getOffersCount())),
    [dispatch],
  );

  // Получаем значения цены
  const priceGte = jsonQuery.price?.value?.gte;
  const priceLte = jsonQuery.price?.value?.lte;

  // Получаем активный тип цены
  const activePriceType = React.useMemo(() => {
    const activeDescriptor = priceTypeDescriptors.find(descriptor => isDescriptorActive(descriptor)(jsonQuery));

    /* istanbul ignore next */
    return activeDescriptor ? activeDescriptor.state : 'price_sm_all';
  }, [jsonQuery]);

  // Опции для Chips
  const priceTypeOptions = React.useMemo(
    () =>
      priceTypeDescriptors
        .map(({ state, label }) => ({
          label,
          value: state,
        }))
        .reverse(),
    [],
  );

  // Обработчик изменения цены
  const handlePriceChange = React.useCallback(
    (type: 'gte' | 'lte') => (value: number | undefined) => {
      const descriptor = priceInputDescriptors[0];
      dispatch({
        type: `${descriptor.name}/${descriptor.state}`,
        subType: type,
        value,
      });
      dispatch(updateTags());
      debouncedGetOffersCount();
    },
    [dispatch, debouncedGetOffersCount],
  );

  // Обработчик изменения типа цены
  const handlePriceTypeClick = React.useCallback(
    (value: string) => {
      const descriptor = priceTypeDescriptors.find(d => d.state === value);
      /* istanbul ignore else */
      if (descriptor) {
        dispatch({
          type: `${descriptor.name}/${descriptor.state}`,
          value,
        });
        dispatch(updateTags());
        dispatch(getOffersCount());
      }
    },
    [dispatch],
  );

  return (
    <FilterPriceWrapper>
      <InputsMultiple
        stack={[
          {
            leftAdornment: 'от',
            rightAdornment: '₽',
            'aria-label': 'Минимальная цена квартиры',
            value: priceGte,
            onChangeValue: handlePriceChange('gte'),
          },
          {
            leftAdornment: 'до',
            rightAdornment: '₽',
            'aria-label': 'Максимальная цена квартиры',
            value: priceLte,
            onChangeValue: handlePriceChange('lte'),
          },
        ]}
      />
      <Chips
        options={priceTypeOptions}
        size="M"
        value={[activePriceType]}
        selectType="single"
        onChipClick={handlePriceTypeClick}
      />
    </FilterPriceWrapper>
  );
});

PriceFilter.displayName = 'PriceFilter';
